import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CloseIcon, FacebookIcon, GoogleIcon } from '../../styles';
import {
  AlternativeButton,
  Content,
  ContentWrapper,
  ErrorMessage,
  Field,
  Header,
  Label,
  Media,
  OrText,
  OrTextBorder,
  StyledInput,
  SubmitButton,
  SubTitle,
  SwitchModeLink,
  Title
} from './styles';

import { FacebookAuthProvider, getAuth, GoogleAuthProvider, signInWithCredential, signInWithPopup } from 'firebase/auth';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { useAppContext } from '../../../context/state';
import { getAuthErrorMessage, getUserData, signUpUser } from '../../../firebase/user';
import { close, getIsFetching, setIsFetching, setShowSignIn } from '../../../store/user';
import { useHomePageTag } from '../../../tag-manager/homepage';
import TermsCheckbox, { SIGN_UP_DOCUMENTS, TERMS_ERROR } from '../TermsCheckbox';
import TermsStep, { socialSignIn } from './TermsStep';

const SignupModal = () => {
  const dispatch = useDispatch();
  const onCloseClick = () => dispatch(close());
  const onSignInClick = () => {
    dispatch(setShowSignIn());
  };

  const isFetching = useSelector(getIsFetching);
  const { setUserData } = useAppContext();

  const [socialId, setSocialId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const submitBtn = useRef(null);

  // Billing B4.4: the box starts empty; no account until it is ticked.
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsError, setTermsError] = useState('');
  const onTermsChange = (checked) => {
    setTermsChecked(checked);
    setTermsError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (firstName.trim() === '' || lastName.trim() === '' || email.trim() === '' || password.trim() === '') {
      setError('Please fill out all fields');
    } else if (!termsChecked) {
      setTermsError(TERMS_ERROR);
    } else {
      dispatch(setIsFetching(true));
      signUpUser(
        { socialId, firstName, lastName, email, password },
        () => {
          useHomePageTag({ label: 'button sign up' });
          getUserData(setUserData);

          dispatch(setIsFetching(false));
          onCloseClick();
        },
        (error) => {
          dispatch(setIsFetching(false));
          setError(error);
        }
      );
    }
  };

  // Billing B4.4: a new Google or Facebook account goes to the terms step first.
  const [newSocialUser, setNewSocialUser] = useState(null);

  const finishSocialSignIn = async (signIn, label) => {
    try {
      dispatch(setIsFetching(true));
      const { user, isNew } = await socialSignIn(signIn);
      dispatch(setIsFetching(false));
      if (isNew) {
        setNewSocialUser(user);
        return;
      }
      getUserData(setUserData);
      useHomePageTag({ label });
      onCloseClick();
    } catch (error) {
      console.error('Social sign-up error:', error);
      dispatch(setIsFetching(false));
      setError(getAuthErrorMessage(error, 'An error occurred during sign up. Please try again.'));
    }
  };

  const handleFacebookResponse = (response) => {
    if (!response.accessToken) {
      setError('Facebook login failed. Please try again.');
      return;
    }
    finishSocialSignIn(() => signInWithCredential(getAuth(), FacebookAuthProvider.credential(response.accessToken)), 'button sign up with facebook');
  };

  const handleGoogleSignUp = () => finishSocialSignIn(() => signInWithPopup(getAuth(), new GoogleAuthProvider()), 'button sign up with google');

  if (newSocialUser) {
    return (
      <TermsStep
        user={newSocialUser}
        onDone={() => {
          getUserData(setUserData);
          useHomePageTag({ label: 'sign up after terms step' });
          onCloseClick();
        }}
        onCancel={onCloseClick}
      />
    );
  }

  return (
    <ContentWrapper>
      <Media />
      <Content>
        <Header>
          <div>
            <Title>Sign Up</Title>
            <SubTitle>Welcome to Yedding! Select method to sign in:</SubTitle>
          </div>
          <button onClick={onCloseClick}>
            <CloseIcon />
          </button>
        </Header>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>First Name</Label>
            <StyledInput type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" />
          </Field>
          <Field>
            <Label>Last Name</Label>
            <StyledInput type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
          </Field>
          <Field>
            <Label>Email</Label>
            <StyledInput type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="janedoe@gmail.com" />
          </Field>
          <Field>
            <Label>Password</Label>
            <StyledInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="**************" />
          </Field>
          <TermsCheckbox id="signup-terms" checked={termsChecked} onChange={onTermsChange} documents={SIGN_UP_DOCUMENTS} error={termsError} />
          {!!error && <ErrorMessage>{error}</ErrorMessage>}
          <SubmitButton type="submit" disabled={isFetching} value={isFetching ? 'Loading...' : 'Sign Up'} />
        </form>
        <OrText>
          <OrTextBorder style={{ left: 0 }} />
          <OrTextBorder style={{ right: 0 }} />
          OR
        </OrText>
        <AlternativeButton onClick={handleGoogleSignUp}>
          <GoogleIcon />
          Sign Up with Google
        </AlternativeButton>
        <FacebookLogin
          appId="1091106402925547"
          autoLoad={false}
          fields="name,email,picture"
          scope="public_profile,email"
          callback={handleFacebookResponse}
          render={(renderProps) => (
            <AlternativeButton onClick={renderProps.onClick} disabled={isFetching}>
              <FacebookIcon />
              Sign Up with Facebook
            </AlternativeButton>
          )}
        />
        <SwitchModeLink>
          Already have an account?
          <button onClick={onSignInClick}>Sign In</button>
        </SwitchModeLink>
      </Content>
    </ContentWrapper>
  );
};

export default SignupModal;
